import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { CalculatorIcon } from '@heroicons/react/24/outline';

const MathOperationNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const operation = data.config?.operation || 'add';

  const getOperationSymbol = (op: string) => {
    const symbols: Record<string, string> = {
      add: '+',
      subtract: '−',
      multiply: '×',
      divide: '÷',
      power: '^',
      modulo: '%',
      sqrt: '√',
      abs: '|x|',
      round: '≈',
      floor: '⌊x⌋',
      ceil: '⌈x⌉'
    };
    return symbols[op] || op;
  };

  const getOperationLabel = (op: string) => {
    const labels: Record<string, string> = {
      add: 'Összeadás',
      subtract: 'Kivonás',
      multiply: 'Szorzás',
      divide: 'Osztás',
      power: 'Hatványozás',
      modulo: 'Maradék',
      sqrt: 'Négyzetgyök',
      abs: 'Abszolút érték',
      round: 'Kerekítés',
      floor: 'Lefelé kerekítés',
      ceil: 'Felfelé kerekítés'
    };
    return labels[op] || op;
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 border-2 transition-all duration-200 ${
      selected ? 'border-orange-600 shadow-xl scale-105' : 'border-orange-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <CalculatorIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-orange-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Operation display */}
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-lg font-bold text-white">
              {getOperationSymbol(operation)}
            </span>
            <span className="text-xs text-orange-100">
              {getOperationLabel(operation)}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Calculation result preview */}
      {data.executionData && typeof data.executionData.result === 'number' && (
        <div className="mt-2 text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <strong>Eredmény:</strong> {data.executionData.result}
          {data.executionData.inputs && (
            <div className="text-xs mt-1 opacity-75">
              {data.executionData.inputs.a} {getOperationSymbol(operation)} {data.executionData.inputs.b}
            </div>
          )}
        </div>
      )}

      {/* Input handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="a"
        className="w-3 h-3 bg-orange-600 border-2 border-white"
        style={{ top: '40%' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="b"
        className="w-3 h-3 bg-orange-600 border-2 border-white"
        style={{ top: '60%' }}
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-orange-600 border-2 border-white"
      />
    </div>
  );
};

export default MathOperationNode;
